import React, { useEffect, useState, createContext, useContext } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import HelpChat from './components/HelpChat';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminSeasons from './pages/AdminSeasons';
import AdminAgeGroups from './pages/AdminAgeGroups';
import Home from './pages/Home';
import OnboardingPage from './pages/OnboardingPage';
import CreateTeam from './pages/CreateTeam';
import EditTeam from './pages/EditTeam';
import CreateGraphic from './pages/CreateGraphic';
import TeamPortal from './pages/TeamPortal';
import './styles/forms.css';
import './styles/modals.css';
import AccountSettings from './pages/AccountSettings';
import Membership from './pages/Membership';
import './App.css';
import AdminHome from './pages/AdminHome';
import AdminReports from './pages/AdminReports';
import AdminAnnouncements from './pages/AdminAnnouncements';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserDetail from './pages/AdminUserDetail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AcceptInvite from './pages/AcceptInvite';
import OrganizationSettings from './pages/OrganizationSettings';
import BulkImport from './pages/BulkImport';
import ManageOrgTeams from './pages/ManageOrgTeams';
import RecentlyDeleted from './pages/RecentlyDeleted';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import CookiePolicy from './pages/CookiePolicy';
import TermsOfService from './pages/TermsOfService';
import AboutUs from './pages/AboutUs';
import Pricing from './pages/Pricing';
import TrackerPage from './pages/TrackerPage';
import PlayerListPage from './pages/analytics/PlayerListPage';
import PlayerProfilePage from './pages/analytics/PlayerProfilePage';
import GameHistoryPage from './pages/analytics/GameHistoryPage';
import GameDetailPage from './pages/analytics/GameDetailPage';
import PlayerDashboardPage from './pages/analytics/PlayerDashboardPage';
import BragCardPage from './pages/analytics/BragCardPage';
import BragCardsListPage from './pages/analytics/BragCardsListPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// PWA install prompt — captured at module level so it survives remounts
let _installPromptEvent = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _installPromptEvent = e;
  });
}

export const PWAInstallContext = createContext(null);

export function usePWAInstall() {
  return useContext(PWAInstallContext);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.gtag) {
      window.gtag('event', 'page_view', { page_path: pathname });
      if (pathname === '/membership') {
        window.gtag('event', 'membership_page_view');
      }
    }
  }, [pathname]);
  return null;
}

function ConditionalHelpChat() {
  const { pathname } = useLocation();
  const isAnalyticsPage = pathname.startsWith('/analytics');
  const isBragCardPage = pathname.startsWith('/brag-card');
  if (isAnalyticsPage || isBragCardPage) return null;
  return <HelpChat />;
}

function ProtectedRoute({ children, requireRole = false, redirectIfRoleSet = false }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) return <Navigate to="/login" />;

  // Redirect unset users to onboarding (except the onboarding page itself)
  if (requireRole && user?.role === 'unset') {
    return <Navigate to="/onboarding" />;
  }

  // Redirect already-onboarded users away from onboarding
  if (redirectIfRoleSet && user?.role && user.role !== 'unset') {
    return <Navigate to="/" />;
  }

  return children;
}

function AnalyticsLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
}

function App() {
  const [installPrompt, setInstallPrompt] = useState(_installPromptEvent);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    _installPromptEvent = null;
    setInstallPrompt(null);
  };

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      _installPromptEvent = e;
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  return (
    <PWAInstallContext.Provider value={{ installPrompt, handleInstallClick }}>
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/pricing" element={<Pricing />} />

          {/* Onboarding */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute redirectIfRoleSet>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />

          {/* New Home page at root */}
          <Route
            path="/"
            element={
              <ProtectedRoute requireRole>
                <Home />
              </ProtectedRoute>
            }
          />

          <Route
            path="/create-team"
            element={
              <ProtectedRoute>
                <CreateTeam />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-team/:id"
            element={
              <ProtectedRoute>
                <EditTeam />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recently-deleted"
            element={
              <ProtectedRoute>
                <RecentlyDeleted />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-graphic"
            element={
              <ProtectedRoute>
                <CreateGraphic />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <ProtectedRoute>
                <AdminUserDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/membership"
            element={
              <ProtectedRoute>
                <Membership />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization/settings"
            element={
              <ProtectedRoute>
                <OrganizationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization/bulk-import"
            element={
              <ProtectedRoute>
                <BulkImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization/manage-teams"
            element={
              <ProtectedRoute>
                <ManageOrgTeams />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/announcements"
            element={
              <ProtectedRoute>
                <AdminAnnouncements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/:teamId"
            element={
              <ProtectedRoute>
                <TeamPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seasons"
            element={
              <ProtectedRoute>
                <AdminSeasons />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/agegroups"
            element={
              <ProtectedRoute>
                <AdminAgeGroups />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute>
                <AdminReports />
              </ProtectedRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route
            path="/analytics/tracker"
            element={
              <ProtectedRoute>
                <TrackerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/players"
            element={
              <ProtectedRoute>
                <AnalyticsLayout>
                  <PlayerListPage />
                </AnalyticsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/players/new"
            element={
              <ProtectedRoute>
                <AnalyticsLayout>
                  <PlayerProfilePage />
                </AnalyticsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/players/:id/edit"
            element={
              <ProtectedRoute>
                <AnalyticsLayout>
                  <PlayerProfilePage />
                </AnalyticsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/players/:id/dashboard"
            element={
              <ProtectedRoute>
                <AnalyticsLayout>
                  <PlayerDashboardPage />
                </AnalyticsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/players/:id/games"
            element={
              <ProtectedRoute>
                <AnalyticsLayout>
                  <GameHistoryPage />
                </AnalyticsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/players/:id/games/:gameId"
            element={
              <ProtectedRoute>
                <AnalyticsLayout>
                  <GameDetailPage />
                </AnalyticsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brag-card"
            element={
              <ProtectedRoute>
                <AnalyticsLayout>
                  <BragCardPage />
                </AnalyticsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/players/:id/brag-cards"
            element={
              <ProtectedRoute>
                <AnalyticsLayout>
                  <BragCardsListPage />
                </AnalyticsLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
        <ConditionalHelpChat />
      </Router>
    </AuthProvider>
    </PWAInstallContext.Provider>
  );
}

export default App;
